import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useToken } from "../../../../../account/useToken";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { useUserContext } from "../../../../../context/userContext";
import { LazyLoading } from "../../../../../controls/LazyLoading/LazyLoading";
import { Alert } from "../../../../../controls/alert";
import { ErrorLabel } from "../../../../../controls/error-label";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { useSearchUser } from "../../hooks/useSearchUser";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { User } from "../../models/User";
import {
	setProfilesToUser,
	upsertUser,
} from "../../repositories/UsersRepository";
import { AssetSelector } from "../common/AssetSelector";
import { AuthTypeSelector } from "../common/AuthTypeSelector";
import { ProfilesPicker } from "../common/ProfilesPicker";

type Props = {
	userId: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const EditUser = ({ userId, show, onHide, suffixTitle }: Props) => {
	const [open, setOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [profileError, setprofileError] = useState("");

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		isSuccess,
	} = useSearchUser({
		userId,
	});

	const { getUserId } = useToken();
	const {
		actions: { setDefaultAreaId },
	} = useUserContext();

	const {
		register,
		control,
		setValue,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<User>({ defaultValues: INITIAL_VALUES });

	const profilesIds = watch("ProfilesId");
	const defaultAssetId = watch("DefaultAssetId");

	const handleClose = (
		event?: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const editUser = useMutation({
		mutationFn: async (data: User) => {
			// Step 1: Update the user
			const userUpdatedId = await upsertUser(data);

			// Step 2: Filter out any invalid profile IDs (NaN, null, undefined)
			const validProfileIds = Array.isArray(profilesIds)
				? (profilesIds as number[]).filter((id) => !isNaN(id) && id > 0)
				: [];

			// Step 3: Set the profiles for the user
			await setProfilesToUser({
				UserId: userUpdatedId,
				profileIds: validProfileIds,
			});

			return userUpdatedId;
		},
		onSuccess: async (userUpdatedId: number) => {
			const currentLoggedUserId = getUserId();
			if (currentLoggedUserId === userUpdatedId && defaultAssetId) {
				setDefaultAreaId(defaultAssetId);
			}

			onHide(true);
			setOpen(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onSubmit: SubmitHandler<User> = async (data) => {
		setIsSubmitLoading(true);
		await editUser.mutate(data);
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			if (isSuccess) {
				const userSelected = rows ? rows[0] : null;
				if (userSelected) {
					const profilesIdsString = userSelected!.ProfilesId?.toString() || "";
					const profilesIds = profilesIdsString
						.split(",")
						.map((pId: string) => pId.trim())
						.filter((pId: string) => pId !== "")
						.map((pId: string) => parseInt(pId))
						.filter((pId: number) => !isNaN(pId));

					setValue("UserId", userSelected!.UserId);
					setValue("UserName", userSelected!.UserName);
					setValue("FirstName", userSelected!.FirstName);
					setValue("LastName", userSelected!.LastName);
					setValue("AuthTypeId", userSelected!.AuthTypeId);
					setValue("IsActive", userSelected!.IsActive);
					setValue("LastLogin", userSelected!.LastLogin);
					setValue("DefaultAssetId", userSelected!.DefaultAssetId);
					setValue("ProfilesId", profilesIds);
				}
			}
		}
	}, [show, isSuccess, rows, userId]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Edit User${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<MesfModal
				title="EDIT USER"
				open={show}
				handleClose={() => onHide(false)}
				id="edit-user-modal"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content>
						{isLoading && (
							<Grid2
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "300px" }}
							>
								<LazyLoading />
							</Grid2>
						)}

						{!isLoading && (
							<>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12 }}>
										<TextField
											{...register("UserName", { required: true })}
											label="Username"
											error={!!errors.UserName}
											autoComplete="off"
										/>
										{errors.UserName?.type === "required" && (
											<ErrorLabel text="User name is required" />
										)}
									</Grid2>
									<Grid2 size={{ md: 6, xs: 12 }}>
										<TextField
											{...register("FirstName", { required: true })}
											label="First name"
											variant="outlined"
											error={!!errors.FirstName}
											autoComplete="off"
										/>
										{errors.FirstName?.type === "required" && (
											<ErrorLabel text="First name is required" />
										)}
									</Grid2>
									<Grid2 size={{ md: 6, xs: 12 }}>
										<TextField
											{...register("LastName", { required: true })}
											label="Last name"
											error={!!errors.FirstName}
											autoComplete="off"
										/>
										{errors.LastName?.type === "required" && (
											<ErrorLabel text="Last name is required" />
										)}
									</Grid2>
									<Grid2
										container
										size={{ xs: 12 }}
										spacing={3}
										alignItems="center"
									>
										<Grid2 size={{ md: 6, xs: 12 }}>
											<Controller
												name="ProfilesId"
												control={control}
												rules={{ required: true }}
												render={({ field: { onChange, value } }) => (
													<ProfilesPicker
														onChange={onChange}
														value={value as number[]}
														userIdForDelete={null}
														setprofileError={setprofileError}
													/>
												)}
											/>
											{errors.ProfilesId && (
												<ErrorLabel text="Select a profile(s)" />
											)}
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Controller
												name="AuthTypeId"
												control={control}
												render={({ field: { value, onChange } }) => (
													<AuthTypeSelector value={value} onChange={onChange} />
												)}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Controller
												name="DefaultAssetId"
												control={control}
												render={({ field: { value, onChange } }) => (
													<AssetSelector value={value} onChange={onChange} />
												)}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Controller
												name="IsActive"
												control={control}
												render={({ field }) => (
													<FormControlLabel
														sx={{
															userSelect: "none",
														}}
														control={
															<Checkbox
																checked={field.value}
																onChange={field.onChange}
																name="IsActive"
																color="primary"
															/>
														}
														label="Active"
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					The user was edited successfully
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Editing User"
			/>
			<ErrorModal
				error={profileError}
				onHide={() => setprofileError("")}
				title="Error Choosing Profile"
			/>
		</>
	);
};
